import { useEffect, useMemo } from 'react';

import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useListContext } from 'react-admin';
import { FormProvider } from 'react-hook-form';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { cleanEmpty } from 'utils/cleanEmpty';

import { MerchantShowAccountsFiltersChips } from './MerchantShowAccountsFiltersChips';
import { MerchantShowAccountsFiltersTable } from './MerchantShowAccountsFiltersTable';

export type MerchantsShowAccountsFilterValues = {
  name: string;
  provider: string;
  currency: string;
  gateways: string;
  partner: string;
};

const INITIAL_FILTER_VALUES = {
  name: '',
  provider: '',
  currency: '',
  gateways: '',
  partner: '',
};

export const MerchantShowAccountsFilters = () => {
  const translate = useTypedTranslate();

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<MerchantsShowAccountsFilterValues>({ defaultValues: INITIAL_FILTER_VALUES });

  const { setFilters, displayedFilters } = useListContext();

  useEffect(() => {
    setFilters(appliedFilters, displayedFilters);
  }, [appliedFilters]);

  const isListFilterExist = useMemo(
    () => Object.keys(cleanEmpty(appliedFilters)).length > 0,
    [appliedFilters],
  );

  return (
    <>
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton key="select-column" />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <MerchantShowAccountsFiltersTable />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label={translate('app.buttons.reset')} onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <MerchantShowAccountsFiltersChips />
            <FilterClearButton
              key="clear"
              onClick={() => {
                onReset();
              }}
              visible={isListFilterExist}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
    </>
  );
};
